import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2, Ticket } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import type { VoucherScenario } from '@/lib/transport/voucher-types';

// Issue the PNR group's Uber voucher (#797 slice 3).
//
// The operator chooses WHAT was granted (the scenario) and WHY (the reason) — never
// a coordinate and never a trip count: the scenario derives the budget in the domain,
// and the destination is a REFERENCE (the booked hotel confirmed by Booking, or the
// declared home point cases already geocoded). The validity window defaults to 24 h
// from now; longer disruptions pick 48/72 h.
//
// Deliberately NOT a `window.confirm`, same as every dialog on this card.

export interface VoucherIssueSubmit {
  scenario: VoucherScenario;
  validityHours: number;
  reason: string;
}

const VALIDITY_HOURS = [24, 48, 72] as const;

const SCENARIO_LABEL_KEY: Record<VoucherScenario, string> = {
  HOTEL_OVERNIGHT: 'transport.voucher.scenarioHotelOvernight',
  RIDE_HOME: 'transport.voucher.scenarioRideHome',
  ONE_WAY: 'transport.voucher.scenarioOneWay',
};

export function TransportVoucherIssueDialog({
  scenarios,
  paxCount,
  homeLabel,
  pending,
  error,
  onOpenChange,
  onConfirm,
}: {
  /** The scenarios this group can actually be issued (gated by hotel ref / home point). */
  scenarios: readonly VoucherScenario[];
  paxCount: number;
  /** The declared home locality when RIDE_HOME is on the table (coarse label, never the street). */
  homeLabel?: string | undefined;
  pending: boolean;
  error?: string | null;
  onOpenChange: (open: boolean) => void;
  onConfirm: (input: VoucherIssueSubmit) => void;
}) {
  const { t } = useTranslation('pnrActions');
  const [scenario, setScenario] = useState<VoucherScenario>(scenarios[0] ?? 'HOTEL_OVERNIGHT');
  const [validityHours, setValidityHours] = useState<number>(24);
  const [reason, setReason] = useState('');
  const [validation, setValidation] = useState<string | null>(null);

  const submit = (): void => {
    const trimmed = reason.trim();
    if (!trimmed) {
      setValidation(t('transport.voucher.noReason'));
      return;
    }
    setValidation(null);
    onConfirm({ scenario, validityHours, reason: trimmed });
  };

  return (
    <Dialog open onOpenChange={onOpenChange}>
      <DialogContent data-testid="transport-voucher-issue-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Ticket className="h-4 w-4" aria-hidden /> {t('transport.voucher.issueTitle')}
          </DialogTitle>
          <DialogDescription>
            {t('transport.voucher.issueDescription', { n: paxCount })}
          </DialogDescription>
        </DialogHeader>

        <label className="block text-sm">
          <span className="mb-1 block font-medium">{t('transport.voucher.scenarioLabel')}</span>
          <select
            value={scenario}
            onChange={(e) => setScenario(e.target.value as VoucherScenario)}
            className="w-full rounded-md border border-input bg-background p-2 text-sm"
            data-testid="transport-voucher-issue-scenario"
          >
            {scenarios.map((option) => (
              <option key={option} value={option}>
                {t(SCENARIO_LABEL_KEY[option])}
              </option>
            ))}
          </select>
        </label>

        <p className="text-xs text-muted-foreground" data-testid="transport-voucher-issue-destination">
          {scenario === 'RIDE_HOME'
            ? t('transport.voucher.destinationHome', { label: homeLabel ?? t('transport.voucher.homeFallback') })
            : t('transport.voucher.destinationHotel')}
        </p>

        <label className="block text-sm">
          <span className="mb-1 block font-medium">{t('transport.voucher.windowLabel')}</span>
          <select
            value={validityHours}
            onChange={(e) => setValidityHours(Number(e.target.value))}
            className="w-full rounded-md border border-input bg-background p-2 text-sm"
            data-testid="transport-voucher-issue-hours"
          >
            {VALIDITY_HOURS.map((hours) => (
              <option key={hours} value={hours}>
                {t('transport.voucher.windowHours', { n: hours })}
              </option>
            ))}
          </select>
        </label>

        <label className="block text-sm">
          <span className="mb-1 block font-medium">{t('reason.requiredLabel')}</span>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={3}
            placeholder={t('transport.voucher.reasonPlaceholderIssue')}
            className="w-full rounded-md border border-input bg-background p-2 text-sm"
            data-testid="transport-voucher-issue-reason"
          />
        </label>

        {validation ? (
          <p className="text-xs text-destructive" role="alert">
            {validation}
          </p>
        ) : null}
        {error ? (
          <p className="text-xs text-destructive" role="alert" data-testid="transport-voucher-issue-error">
            {error}
          </p>
        ) : null}

        <DialogFooter>
          <Button type="button" variant="ghost" size="sm" onClick={() => onOpenChange(false)}>
            {t('transport.voucher.issueDismiss')}
          </Button>
          <Button
            type="button"
            size="sm"
            disabled={pending}
            onClick={submit}
            data-testid="transport-voucher-issue-confirm"
          >
            {pending ? <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden /> : null}
            {t('transport.voucher.issueConfirm')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
